import React, {CSSProperties, useMemo} from 'react';
import styled from 'styled-components';
import {AstView, EnrichedAstNode} from 'rendition-core/types';
import {Highlighter} from './Highlighter';
import {ScalablePreviewFixed} from './ScalablePreviewFixed';
import {ScalablePreview} from './ScalablePreview';
import {RichPreview} from './RichPreview';
import {findNodeById, findParentOfNodeById} from 'rendition-core';
import {RightClickMenu} from 'Components/RightClickMenu';
import {ColorPicker} from 'Components/ColorPicker';
import {useColorPicker} from 'Hooks/useColorPicker';
import {useProductContext} from 'Contexts/ProductContext';

interface Props {
    view: AstView;
    areaSize: number;
    isolatedView?: boolean;
    rootNode: EnrichedAstNode | null;
    forceFixedSizing?: boolean;
    overLayedChildren?: React.ReactNode;
    resizable?: boolean;
    rendersSelectedNodeAsRoot?: boolean;
    onNodeSelection?: (ids: string[]) => void;
    infiniteCanvas?: boolean;
    selectedNodes?: EnrichedAstNode[];
    hideTopMenu?: boolean;
    canvasInitialOffsetX?: number;
}

export const DocumentPreviewer: React.FC<React.PropsWithChildren<Props>> = ({
    view,
    areaSize,
    isolatedView = false,
    rootNode,
    resizable = false,
    overLayedChildren,
    forceFixedSizing = false,
    children,
    rendersSelectedNodeAsRoot = false,
    onNodeSelection,
    infiniteCanvas = true,
    selectedNodes = [],
    hideTopMenu = false,
    canvasInitialOffsetX = 0,
}) => {
    const {devMode} = useProductContext();

    const viewBackground = useMemo(() => {
        const getBackground = (nodeId: string) => {
            const parent = findParentOfNodeById(view.tree, nodeId);
            if (parent) {
                if (parent.styles.backgroundColor) {
                    return parent.styles.backgroundColor;
                } else {
                    return getBackground(parent.internalId);
                }
            } else {
                const node = findNodeById(view.tree, nodeId);
                return node?.styles.backgroundColor;
            }
        };

        return getBackground(rootNode?.internalId || view.tree.internalId);
    }, [rootNode?.internalId, view.tree]);

    const {
        color: backgroundColor,
        onChange,
        toggleColorPicker,
        showColorPicker,
    } = useColorPicker(viewBackground || view.meta.compilerOptions.previewBackground || '#000000', true);

    const hideColorPicker = viewBackground || rootNode?.styles.backgroundImage;

    const ScalablePreviewComponent = infiniteCanvas ? ScalablePreview : ScalablePreviewFixed;

    return (
        <>
            <div style={{position: 'relative'}}>
                <ScalablePreviewComponent
                    view={view}
                    background={backgroundColor}
                    previewAreaSize={areaSize}
                    resizable={resizable}
                    rootNode={rootNode || view.tree}
                    onNodeSelection={onNodeSelection}
                    overLayedChildren={
                        <>
                            <Highlighter />
                            {overLayedChildren}
                        </>
                    }
                    hideTopMenu={hideTopMenu}
                    rendersSelectedNodeAsRoot={rendersSelectedNodeAsRoot}
                    canvasInitialOffsetX={canvasInitialOffsetX}
                >
                    <StopInheritance
                        style={
                            rootNode && isolatedView
                                ? relevantParentCss(findParentOfNodeById(view.tree, rootNode.internalId))
                                : {}
                        }
                    >
                        {devMode && (
                            <div className="font-xxl text-white">
                                Internal Image
                                <RichPreview
                                    forceFixedSizing={forceFixedSizing}
                                    view={view}
                                    fixedPosition={true}
                                    isolatedNode={isolatedView ? rootNode : null}
                                />
                                <div className="h-10 bg-black" />
                                User View
                            </div>
                        )}

                        <RichPreview
                            forceFixedSizing={forceFixedSizing}
                            view={view}
                            fixedPosition={false}
                            isolatedNode={isolatedView ? rootNode : null}
                        />
                    </StopInheritance>
                </ScalablePreviewComponent>
                {!hideColorPicker && (
                    <ColorPicker
                        style={{position: 'absolute', top: -50, right: 0, border: '1px solid #fff', borderRadius: 4}}
                        color={backgroundColor}
                        showColorPicker={showColorPicker}
                        onChange={onChange}
                        toggleColorPicker={toggleColorPicker}
                        pickerRight="0px"
                    />
                )}
                {children}
            </div>
            <RightClickMenu view={view} selectedNodes={selectedNodes} />
        </>
    );
};

const relevantParentCss = (node: EnrichedAstNode | undefined): CSSProperties => {
    const keys = ['display', 'flexDirection', 'justifyContent'];

    const css: CSSProperties = {};
    if (!node) {
        return css;
    }
    keys.forEach((key) => {
        if (node.styles[key]) {
            css[key] = node.styles[key];
        }
    });
    return css;
};

const StopInheritance = styled.div`
    all: initial;
    box-sizing: content-box;
`;
